import React from "react";
import { Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import InputMask from "react-input-mask";
import Select from "react-select";
import moment from "moment-timezone";
import { format_date_simple } from "../../../../../helpers/api";

export default function BranchStepLandlord({
  step,
  changeStep,
  register,
  control,
  errors,
  isValid,
  trigger,
  branch_row,
  states,
}) {
  const validateAge = (value) => {
    if (!value || typeof value !== "string") return "Invalid input";

    const cleanedValue = value.replace(/_/g, "").trim();

    if (cleanedValue.length !== 10) return "Date must be in MM-DD-YYYY format";

    const enteredDate = moment(cleanedValue, "MM-DD-YYYY", true);

    if (!enteredDate.isValid()) return "Invalid date format";

    const minDate = moment().subtract(18, "years");

    if (enteredDate.isAfter(minDate)) {
      return "Must be at least 18 years old";
    }

    return true;
  };

  return (
    <>
      <fieldset>
        <h4 className="screening_heading_af">Landlord Information</h4>
        <div className="form_row row flex">
          <div className="col-xs-4">
            <h6>First Name</h6>
            <div className="form_blk">
              <input
                type="text"
                className="txtBox"
                defaultValue={branch_row?.landlord_first_name || ""}
                {...register("landlord_first_name", { required: "Required" })}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_first_name"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
          <div className="col-xs-4">
            <h6>Middle Name</h6>
            <div className="form_blk">
              <input
                type="text"
                className="txtBox"
                defaultValue={branch_row?.landlord_middle_name || ""}
                {...register("landlord_middle_name")}
              />
            </div>
          </div>
          <div className="col-xs-4">
            <h6>Last Name</h6>
            <div className="form_blk">
              <input
                type="text"
                className="txtBox"
                defaultValue={branch_row?.landlord_last_name || ""}
                {...register("landlord_last_name", { required: "Required" })}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_last_name"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
          <div className="col-xs-4">
            <h6>Date of Birth</h6>
            <div className="form_blk">
              <Controller
                name="landlord_dob"
                control={control}
                defaultValue={
                  branch_row?.landlord_dob
                    ? format_date_simple(branch_row?.landlord_dob, "MM-DD-YYYY")
                    : ""
                }
                rules={{
                  required: "Date of Birth is required",
                  validate: validateAge,
                }}
                render={({ field }) => (
                  <InputMask
                    {...field}
                    mask="99-99-9999"
                    placeholder="MM-DD-YYYY"
                    className="txtBox"
                    autoComplete="off"
                    value={field.value || ""}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_dob"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
          <div className="col-xs-6">
            <h6>Phone Number</h6>
            <div className="form_blk">
              <Controller
                name="landlord_phone"
                control={control}
                defaultValue={branch_row?.landlord_phone || ""}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <InputMask
                    mask="+1 (999) 999-9999"
                    id="landlord_phone"
                    className="txtBox"
                    autoComplete="none"
                    {...field}
                    value={field.value || ""}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_phone"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
          <div className="col-xs-6">
            <h6>Address Line 1</h6>
            <div className="form_blk">
              <input
                type="text"
                className="txtBox"
                defaultValue={branch_row?.landlord_address_line1 || ""}
                {...register("landlord_address_line1", { required: "Required" })}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_address_line1"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
          <div className="col-xs-6">
            <h6>Address Line 2</h6>
            <div className="form_blk">
              <input
                type="text"
                className="txtBox"
                defaultValue={branch_row?.landlord_address_line2 || ""}
                {...register("landlord_address_line2")}
              />
            </div>
          </div>
          <div className="col-xs-4">
            <h6>City</h6>
            <div className="form_blk">
              <input
                type="text"
                className="txtBox"
                defaultValue={branch_row?.landlord_city || ""}
                {...register("landlord_city", { required: "Required" })}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_city"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
          <div className="col-xs-4">
            <h6>State</h6>
            <div className="form_blk">
              <Controller
                name="landlord_state"
                control={control}
                defaultValue={branch_row?.landlord_state || ""}
                rules={{ required: "Required" }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={states}
                    onChange={(val) => onChange(val.value)}
                    label="Select State"
                    defaultValue={states?.filter((option) => option.value === value)}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_state"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
          <div className="col-xs-4">
            <h6>ZIP Code</h6>
            <div className="form_blk">
              <input
                type="text"
                className="txtBox"
                defaultValue={branch_row?.landlord_zip_code || ""}
                {...register("landlord_zip_code", {
                  required: "Required",
                  minLength: { value: 5, message: "Zip code should be equal to 5 digits" },
                  maxLength: { value: 5, message: "Zip code should be equal to 5 digits" },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="landlord_zip_code"
                render={({ message }) => (
                  <p className="error">
                    <i className="fi-warning"></i> {message}
                  </p>
                )}
              />
            </div>
          </div>
        </div>
        <div className="cmnBtn text-center">
          <button
            type="button"
            className="webBtn backBtn"
            onClick={() => changeStep(step - 1)}
          >
            Previous
          </button>
          {!isValid ? (
            <button
              type="button"
              className="webBtn colorBtn nextBtn"
              onClick={() => trigger()}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="webBtn colorBtn nextBtn"
              onClick={() => changeStep(step + 1)}
            >
              Next
            </button>
          )}
        </div>
      </fieldset>
    </>
  );
}
